<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, FormInput, UiModalHeader } from '@/shared/ui';
  import { DIRECTORY_TITLE_MAX_LENGTH } from '../config/directoryTitle';
  import {
    type CreateFolderSubmitPayload,
    useCreateFolderForm,
  } from '../model/useCreateFolderForm';

  const props = withDefaults(
    defineProps<{
      isSubmitting?: boolean;
      titleError?: string;
    }>(),
    {
      isSubmitting: false,
      titleError: '',
    }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'clearTitleError'): void;
    (e: 'submit', payload: CreateFolderSubmitPayload): void;
  }>();

  const title = ref('');

  const { reset, buildSubmitPayload, isTitleValid } = useCreateFolderForm(title);

  const isSubmitDisabled = computed(() => !isTitleValid.value || props.isSubmitting);

  const handleTitleInput = (value: string) => {
    title.value = value;
    if (props.titleError) {
      emit('clearTitleError');
    }
  };

  const handleClose = () => {
    emit('close');
  };

  const handleSubmit = () => {
    if (isSubmitDisabled.value) {
      return;
    }
    emit('submit', buildSubmitPayload());
  };

  const handleClosed = () => {
    reset();
  };
</script>

<template>
  <VueFinalModal
    class="flex items-center justify-center"
    content-class="flex w-[600px] max-w-[calc(100vw-2rem)] flex-col rounded-2xl bg-white-01 shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    click-to-close
    @closed="handleClosed"
  >
    <div
      data-testid="create-folder-modal"
      class="flex flex-col"
    >
      <UiModalHeader
        class="flex-shrink-0 border-b border-gray-02 px-6 py-4 text-sm"
        title="Criar Pasta"
        @close="handleClose"
      />

      <div
        data-testid="create-folder-fields-row"
        class="px-6 py-6"
      >
        <FormInput
          id="create-folder-title"
          label="Título da Pasta"
          type="text"
          :model-value="title"
          placeholder="Escreva um título que represente a pasta"
          :max-length="DIRECTORY_TITLE_MAX_LENGTH"
          :error="titleError"
          @update:model-value="handleTitleInput"
        />
      </div>

      <div class="flex flex-shrink-0 justify-end gap-3 border-t border-gray-02 px-6 py-4">
        <ColoredButton
          text="Cancelar"
          outlined
          color="gray-03"
          :disabled="isSubmitting"
          @click="handleClose"
        />
        <ColoredButton
          data-testid="create-folder-submit"
          text="Criar"
          :disabled="isSubmitDisabled"
          @click="handleSubmit"
        />
      </div>
    </div>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
